
// components/Footer.tsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, LayoutDashboard } from "lucide-react";
import footerlogo from "@/assets/mylistiq-logo-updated.png";

const CallToActionFooter: React.FC = () => {
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem("access_token");

  return (
    <>
      {/* Conditional CTA Section */}
      {!isLoggedIn ? (
        <section className="py-20 bg-[#184ccc]">
          <div className="mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Email Marketing?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Join thousands of successful agents who've already streamlined
              their marketing with MyListIQ.
            </p>

            {/* Buttons - Not Logged In */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8">
              <Button asChild variant="secondary" size="lg">
                <Link to="/signup" className="text-base font-medium">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg">
                <Link
                  to="/signup"
                  className="text-black border-white transition-colors"
                >
                  Sign Up
                </Link>
              </Button>
            </div>

            {/* Trust badges */}
            {/* <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-8">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-white font-medium">7-day free trial</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-white font-medium">Cancel anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span className="text-white font-medium">
                  Setup in minutes
                </span>
              </div>
            </div> */}
          </div>
        </section>
      ) : (
        <section className="py-20 bg-[#184ccc]">
          <div className="mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Email Marketing?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Join thousands of successful agents who've already streamlined
              their marketing with MyListIQ. Start your free trial today
            </p>

            <Button
              onClick={() => navigate("/dashboard")}
              variant="secondary"
              size="lg"
              className="text-lg font-semibold px-8"
            >
              <LayoutDashboard className="mr-3 h-5 w-5" />
              Go to Dashboard
            </Button>
          </div>
        </section>
      )}

      {/* Footer - Always visible */}
      <footer className="bg-[#184ccc] py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 4 Column Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Column 1: Logo & Description */}
            <div className="space-y-4">
              <img
                src={footerlogo}
                alt="MyListIQ - Easy. Affordable. Smarter."
                className="h-12 w-auto"
              />
              <p className="text-white/80 max-w-xs">
                Professional email marketing solutions for real estate
                professionals.
              </p>
            </div>

            {/* Column 2: Company Links */}
            <div>
              <h3 className="font-semibold text-white mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/about"
                    className="text-white/80 hover:text-white transition"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-white/80 hover:text-white transition"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white transition"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white transition"
                  >
                    Terms &amp; Conditions
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 3: Our Services */}
            <div>
              <h3 className="font-semibold text-white mb-4">Our Services</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/help"
                    className="text-white/80 hover:text-white transition"
                  >
                    Email Marketing
                  </Link>
                </li>
                <li>
                  <Link
                    to="/launches"
                    className="text-white/80 hover:text-white transition"
                  >
                    Targeted Launches
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard"
                    className="text-white/80 hover:text-white transition"
                  >
                    Smart by AI
                  </Link>
                </li>
                <li>
                  <Link
                    to="/analytics"
                    className="text-white/80 hover:text-white transition"
                  >
                    Analytics & Insights
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 4: Contact / Office */}
            <div>
              <h3 className="font-semibold text-white mb-4">Contact</h3>
              <div className="text-white/80 space-y-1">
                <p>5366 Central Florida Pkwy #1015</p>
                <p>Orlando, FL 32821</p>
                <br />
               
                <p>support@mylistiq.com</p>
                 <p>+1 (321) 519-4247</p>
              </div>
            </div>
          </div>

          {/* Bottom Section: Copyright */}
          <div className="border-t border-white/20 mt-12 pt-8 text-center">
            <p className="text-white/80 text-sm">
              © 2025 MyListIQ. All rights reserved. Built for professionals who
              demand excellence.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default CallToActionFooter;